/**
 * ScopeAIIntro — conversational 3-step onboarding card shown BEFORE the
 * drawing canvas. Replaces the "blank canvas drop-in" experience with an
 * AI-style briefing that sets expectations and asks the user whether they
 * have a floor plan or will measure by dimensions.
 *
 * Port of the P7 prototype's "Scope AI" introduction.
 *
 * Props:
 *   onStart({ path }) — called when the user picks a path ('plan' | 'dimensions')
 *   onSkip() — user dismisses the intro to go directly to canvas
 */
import React, { useState } from 'react';
import { Sparkles, Upload, Ruler, ChevronRight, X } from 'lucide-react';

export const ScopeAIIntro = ({ onStart, onSkip }) => {
  const [step, setStep] = useState(0);
  const [path, setPath] = useState(null);  // 'plan' | 'dimensions'

  const steps = [
    {
      title: "Let's build your Schedule of Works.",
      body: "Before we start — let's make sure you have what you need. To measure your property we need either a floor plan or the dimensions of each room. A floor plan gives the most accurate result — even an estate agent PDF works well.",
      cta: null,
    },
    {
      title: path === 'plan'
        ? 'Upload your floor plan and we will set up the drawing canvas.'
        : 'No floor plan — no problem.',
      body: path === 'plan'
        ? 'Accepted formats: JPG, PNG or PDF. Estate agent floor plans work perfectly.'
        : "We will guide you through each room one at a time. You type the length and width and we handle the maths.",
      cta: null,
    },
    // Scale step — drawing path ONLY. Suppressed for dimensions path (§3.2).
    ...(path === 'plan' ? [{
      title: 'Set the scale before you start drawing.',
      body: "Once your plan is uploaded, click two points on a wall or dimension line you know the real length of. It takes 10 seconds and it's the difference between an accurate Schedule of Works and a wrong one.",
      cta: null,
    }] : []),
  ];

  const pickPath = (p) => {
    setPath(p);
    setStep(1);
  };

  const next = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else onStart({ path });
  };

  return (
    <div
      data-testid="scope-ai-intro"
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'rgba(15, 39, 66, 0.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%',
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ background: '#0F2742', color: '#fff', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Sparkles className="w-4 h-4" style={{ color: '#FDE047' }} />
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            SCOPE — setup guide
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.7 }}>
            {/* §7.3 — always show out-of-3 so the counter doesn't jump 1-of-2 → 2-of-3.
                Dimensions path only uses 2 screens but the overall modal is still 3 max. */}
            Step {step + 1} of 3
          </div>
          <button
            type="button"
            onClick={onSkip}
            data-testid="scope-ai-intro-skip"
            aria-label="Skip intro"
            style={{ background: 'transparent', border: 'none', color: '#fff', opacity: 0.7, cursor: 'pointer', padding: 4 }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress pills */}
        <div style={{ display: 'flex', gap: 4, padding: '10px 20px', borderBottom: '1px solid #F1F5F9' }}>
          {steps.map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1, height: 4, borderRadius: 2,
                background: i <= step ? '#1D4ED8' : '#E2E8F0',
                transition: 'background 0.3s',
              }}
            />
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '24px 20px' }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0F2742', margin: 0, marginBottom: 10, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
            {steps[step].title}
          </h2>
          <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: 0 }}>
            {steps[step].body}
          </p>

          {/* Step 0 — path selection */}
          {step === 0 && (
            <div style={{ display: 'grid', gap: 8, marginTop: 18 }}>
              <button
                type="button"
                onClick={() => pickPath('plan')}
                data-testid="scope-ai-intro-path-plan"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  border: '1px solid #1D4ED8', background: '#EFF6FF',
                  borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                }}
              >
                <Upload className="w-4 h-4" style={{ color: '#1D4ED8' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1D4ED8' }}>
                    I have a floor plan <span style={{ fontWeight: 500, color: '#475569', fontSize: 11 }}>— Recommended</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                    Upload a JPG, PNG or PDF — estate agent plans work.
                  </div>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: '#1D4ED8' }} />
              </button>
              <button
                type="button"
                onClick={() => pickPath('dimensions')}
                data-testid="scope-ai-intro-path-dimensions"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  border: '1px solid #CBD5E1', background: '#fff',
                  borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                }}
              >
                <Ruler className="w-4 h-4" style={{ color: '#64748B' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F2742' }}>
                    I will enter room dimensions manually
                  </div>
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                    We guide you through each room one at a time.
                  </div>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />
              </button>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #F1F5F9', background: '#F8FAFC' }}>
          <button
            type="button"
            onClick={onSkip}
            style={{ background: 'transparent', border: 'none', color: '#64748B', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
            data-testid="scope-ai-intro-skip-foot"
          >
            Skip — take me straight to the canvas
          </button>
          {step > 0 && (
            <button
              type="button"
              onClick={next}
              data-testid="scope-ai-intro-next"
              style={{
                background: '#0F2742', color: '#fff', border: 'none',
                padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {step === steps.length - 1 ? 'Got it — start drawing' : 'Next'}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScopeAIIntro;
